import type { BlockGroup, WorkflowBlock } from '@/types/workflow';

export type ParentMap = Map<string, string | null>;

function isSubset(a: Set<string>, b: Set<string>): boolean {
  if (a.size === 0 || a.size > b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

function collectDescendantBlockIds(
  groupId: string,
  groups: BlockGroup[],
  parentMap: ParentMap,
): Set<string> {
  const result = new Set<string>();
  const visit = (id: string) => {
    const g = groups.find(x => x.id === id);
    if (!g) return;
    for (const bid of g.blockIds) result.add(bid);
    for (const child of groups) {
      if (parentMap.get(child.id) === id) visit(child.id);
    }
  };
  visit(groupId);
  return result;
}

/**
 * Infer parent-child relationships from blockIds containment.
 * Group A is the parent of B when:
 *   - B.blockIds ⊂ A.blockIds (or A's recursive block set), and
 *   - no other group C exists with B ⊂ C ⊂ A.
 *
 * Respects existing parentGroupId values — they override inference.
 */
export function inferParentGroupIds(groups: BlockGroup[]): ParentMap {
  const map: ParentMap = new Map();
  const blockSets = new Map<string, Set<string>>();
  for (const g of groups) blockSets.set(g.id, new Set(g.blockIds));

  for (const child of groups) {
    if (child.parentGroupId !== undefined) {
      map.set(child.id, child.parentGroupId ?? null);
      continue;
    }
    const childBlocks = blockSets.get(child.id)!;
    if (childBlocks.size === 0) {
      map.set(child.id, null);
      continue;
    }

    const candidates = groups.filter(p => {
      if (p.id === child.id) return false;
      const pBlocks = blockSets.get(p.id)!;
      return pBlocks.size > childBlocks.size && isSubset(childBlocks, pBlocks);
    });

    if (candidates.length === 0) {
      map.set(child.id, null);
      continue;
    }

    // Smallest superset = nearest parent
    candidates.sort((a, b) => blockSets.get(a.id)!.size - blockSets.get(b.id)!.size);
    map.set(child.id, candidates[0].id);
  }

  return map;
}

/**
 * Return groups with parentGroupId set according to parentMap and with
 * blockIds reduced so each block is owned by the deepest group only.
 */
export function normalizeBlockOwnership(
  groups: BlockGroup[],
  parentMap: ParentMap,
): BlockGroup[] {
  return groups.map(group => {
    const descendantBlocks = new Set<string>();
    for (const other of groups) {
      if (other.id === group.id) continue;
      if (isAncestor(group.id, other.id, parentMap)) {
        for (const bid of other.blockIds) descendantBlocks.add(bid);
      }
    }
    const ownedBlockIds = group.blockIds.filter(bid => !descendantBlocks.has(bid));
    return {
      ...group,
      parentGroupId: parentMap.get(group.id) ?? undefined,
      blockIds: ownedBlockIds,
    };
  });
}

function isAncestor(ancestorId: string, descendantId: string, parentMap: ParentMap): boolean {
  let cur = parentMap.get(descendantId) ?? null;
  while (cur) {
    if (cur === ancestorId) return true;
    cur = parentMap.get(cur) ?? null;
  }
  return false;
}

export function computeDepth(groupId: string, parentMap: ParentMap): number {
  let depth = 0;
  let cur = parentMap.get(groupId) ?? null;
  while (cur) {
    depth++;
    cur = parentMap.get(cur) ?? null;
  }
  return depth;
}

export function getLeafGroupIds(groups: BlockGroup[], parentMap: ParentMap): Set<string> {
  const hasChild = new Set<string>();
  for (const [, parentId] of parentMap) {
    if (parentId) hasChild.add(parentId);
  }
  return new Set(groups.filter(g => !hasChild.has(g.id)).map(g => g.id));
}

/**
 * Compute a default collapsed card position for a group from the average
 * position of its (recursively owned) blocks. Snaps to the 20px grid.
 */
export function computeCollapsedCenter(
  groupId: string,
  groups: BlockGroup[],
  parentMap: ParentMap,
  blocks: WorkflowBlock[],
): { x: number; y: number } {
  const blockIds = collectDescendantBlockIds(groupId, groups, parentMap);
  const owned = blocks.filter(b => blockIds.has(b.id));
  if (owned.length === 0) return { x: 0, y: 0 };
  const avgX = owned.reduce((s, b) => s + b.x, 0) / owned.length;
  const avgY = owned.reduce((s, b) => s + b.y, 0) / owned.length;
  return { x: Math.round(avgX / 20) * 20, y: Math.round(avgY / 20) * 20 };
}
